import { appLinks } from '../lib/crossSiteLinks';
import { isEarlyAccessPlan } from '../lib/billingUi';

type BannerMode = 'guest' | 'free' | 'paid';
type BannerVariant = 'primary' | 'secondary';

interface UpgradeBannerProps {
  mode: BannerMode;
  planCode?: string | null;
  planDisplayName?: string | null;
  packLimitLabel?: string;
  retentionLabel?: string;
  variant?: BannerVariant;
}

const baseStyles = 'rounded-2xl border border-slate-700/60 bg-slate-800/60 p-6 text-slate-200 shadow-xl';
const primaryStyles = 'border-blue-500/40 bg-gradient-to-r from-blue-900/40 to-slate-900/60';

export function UpgradeBanner({
  mode,
  planCode,
  planDisplayName,
  packLimitLabel,
  retentionLabel,
  variant = 'secondary'
}: UpgradeBannerProps) {
  const containerClass = `${baseStyles} ${variant === 'primary' ? primaryStyles : ''}`;
  const isEarlyAccess = isEarlyAccessPlan(planCode, planDisplayName);

  if (mode === 'guest') {
    return (
      <div className={containerClass}>
        <h3 className="text-xl font-bold text-white mb-2">Keep this report available</h3>
        <p className="text-slate-300 mb-4">
          Guest mode creates a one-time evidence pack with short retention. Create an account to explore the
          core platform, keep packs longer, schedule reports, and generate org-wide exports.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={appLinks.register}
            className="inline-flex items-center justify-center bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-500 transition font-semibold"
          >
            Create account
          </a>
          <a
            href={appLinks.login}
            className="inline-flex items-center justify-center border border-slate-600 text-slate-200 px-5 py-2 rounded-lg hover:border-blue-500/60 hover:text-white transition font-semibold"
          >
            Sign in
          </a>
        </div>
      </div>
    );
  }

  if (mode === 'free') {
    return (
      <div className={containerClass}>
        <h3 className="text-xl font-bold text-white mb-2">
          {isEarlyAccess ? 'Early Access feedback' : 'Tools are unlocked on Starter'}
        </h3>
        <p className="text-slate-300 mb-4">
          {isEarlyAccess
            ? 'You are in Early Access. Tell us what evidence you need next, which formats to prioritize, or what limits should change.'
            : `Your current plan includes ${packLimitLabel || '0'} packs/month and no long-term retention. Upgrade to generate evidence packs with retention and org-wide exports.`}
        </p>
        <a
          href={isEarlyAccess ? `${appLinks.settings}#feedback` : appLinks.register}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-500 transition font-semibold"
        >
          {isEarlyAccess ? 'Share feedback' : 'Upgrade to Starter'}
        </a>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <h3 className="text-xl font-bold text-white mb-2">Plan entitlements</h3>
      <p className="text-slate-300 mb-4">
        {planDisplayName || planCode || 'Paid plan'} includes {packLimitLabel || 'pack limits'} and {retentionLabel || 'retention limits'}.
      </p>
      <a href={appLinks.register} className="inline-flex items-center gap-2 border border-slate-600 text-slate-200 px-5 py-2 rounded-lg hover:border-blue-500/60 hover:text-white transition font-semibold">
        Manage billing
      </a>
    </div>
  );
}
